import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, X } from "lucide-react";
import { useDispatch } from "react-redux";
import { GoogleLogin } from "@react-oauth/google";

import "./LoginForm.css";

import { setCredentials } from "../../features/auth/authSlice";

import service from "../../services/service";
import endpoints from "../../services/endpoints";
import { requestNotificationPermission } from "../../services/firebaseNotification";

export default function LoginForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const googleButtonRef = useRef(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Invitation Modal States
  const [invitations, setInvitations] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // ==========================
  // REUSABLE FCM SAVE FUNCTION
  // ==========================
  const saveFcmToken = async (accessToken) => {
    try {
      const fcmToken = await requestNotificationPermission();
      
      if (fcmToken) {
        await service.post(
          endpoints.notifications.saveToken,
          { fcm_token: fcmToken },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            skipToast: true, 
          }
        );
      }
    } catch (fcmError) {
      console.error("FCM TOKEN REGISTRATION ERROR:", fcmError);
    }
  };

  
  const handleAuthInvitations = (responseData) => {
    if (responseData?.invite?.showInvite && responseData?.invite?.invitations?.length > 0) {
      setInvitations(responseData.invite.invitations);
      setShowInviteModal(true);
      return true; 
    }
    return false;
  };

  
  const handleBuyNow = (inviteItem) => {
    setShowInviteModal(false);
    navigate(`/challenges/view/${inviteItem.challengeId}`);
  };

  /* NORMAL LOGIN API */
  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const payload = {
        email,
        password,
      };

      const res = await service.post(
        endpoints.auth.login,
        payload,
        {
          showSuccessToast: true,
        }
      );

      const responseData = res?.data?.data;

      dispatch(
        setCredentials({
          user: responseData?.user || null,
          accessToken: responseData?.accessToken,
          refreshToken: responseData?.refreshToken,
        })
      );

      if (responseData?.accessToken) {
        await saveFcmToken(responseData.accessToken);
      }

      // Check for pending invitations
      const hasInvites = handleAuthInvitations(responseData);
      if (!hasInvites) {
        navigate("/home");
      }
    } catch (error) {
      console.log("LOGIN ERROR", error);
    } finally {
      setLoading(false);
    }
  };

  /* GOOGLE LOGIN */
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setGoogleLoading(true);

      const payload = {
        token: credentialResponse?.credential,
      };

      const res = await service.post(endpoints.auth.googleLogin, payload);

      const responseData = res?.data?.data;

      dispatch(
        setCredentials({
          user: responseData?.user,
          accessToken: responseData?.accessToken,
          refreshToken: responseData?.refreshToken,
        }),
      );

      if (responseData?.accessToken) {
        await saveFcmToken(responseData.accessToken);
      }

      // Check for pending invitations
      const hasInvites = handleAuthInvitations(responseData);
      if (!hasInvites) {
        navigate("/home");
      }
    } catch (error) {
      console.log("GOOGLE LOGIN ERROR", error);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="login-form-wrapper">
      <div className="login-heading">
        <h1>Login</h1>
        <p>Enter Email ID & Password to login</p>
      </div>

      <form className="login-form" onSubmit={handleLogin}>
        {/* Email */}
        <div className="form-group">
          <label>Email ID</label>
          <input
            type="email"
            placeholder="Enter email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        {/* Password */}
        <div className="form-group">
          <label>Password</label>
          <div className="password-field">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <button
              type="button"
              className="eye-btn"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* Forgot */}
        <div className="forgot-password">
          <Link to="/forget-password">Forgot password?</Link>
        </div>

        {/* Login */}
        <button type="submit" className="auth-login-btn" disabled={loading}>
          {loading ? "Logging in..." : "Login"}
        </button>
      </form>

      {/* Divider */}
      <div className="divider">
        <div></div>
        <span>or</span>
        <div></div>
      </div>

      <button
        type="button"
        className="google-btn"
        disabled={googleLoading}
        onClick={() => {
          const googleButton =
            googleButtonRef.current?.querySelector("div[role=button]");

          if (googleButton) {
            googleButton.click();
          }
        }}
      >
        {googleLoading ? "Please wait..." : "Continue with Google"}
      </button>

      {/* HIDDEN GOOGLE BUTTON */}
      <div
        ref={googleButtonRef}
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
        }}
      >
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => {
            console.log("Google Login Failed");
          }}
        />
      </div>

      {/* Signup */}
      <div className="signup-link">
        <p>
          Didn&apos;t have an account?{" "}
          <Link to="/create-account">Create an Account</Link>
        </p>
      </div>

      {/* INVITATIONS OVERLAY MODAL */}
      {showInviteModal && (
        <div className="challenge-modal-overlay" style={{ zIndex: 1000 }}>
          <div className="challenge-success-modal" style={{ maxWidth: "500px", width: "100%" }}>
            <button
              className="challenge-modal-close"
              onClick={() => {
                setShowInviteModal(false);
                navigate("/home");
              }}
            >
              <X size={18} />
            </button>

            <div className="challenge-success-icon">🎉</div>
            <h2>Pending Team Invites!</h2>
            <p style={{ marginBottom: "20px" }}>You have been invited to join the following challenge teams:</p>

            <div style={{ maxHeight: "250px", overflowY: "auto", marginBottom: "20px", paddingRight: "5px" }}>
              {invitations.map((invite) => (
                <div 
                  key={invite.inviteId} 
                  style={{
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    padding: "12px 15px",
                    background: "#f9f9f9",
                    borderRadius: "8px",
                    marginBottom: "10px",
                    border: "1px solid #eee"
                  }}
                >
                  <div style={{ textAlign: "left" }}>
                    <h4 style={{ margin: "0 0 4px 0", color: "#222" }}>{invite.teamName}</h4>
                    <span style={{ fontSize: "12px", color: "#666" }}>Challenge ID: {invite.challengeId}</span>
                  </div>
                  
                  <button
                    type="button"
                    className="red-btn"
                    onClick={() => handleBuyNow(invite)}
                    style={{
                      padding: "8px 16px",
                      fontSize: "13px",
                      borderRadius: "6px",
                      height: "auto",
                      minWidth: "100px"
                    }}
                  >
                    Buy Now
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                color: "#666",
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: "14px"
              }}
              onClick={() => {
                setShowInviteModal(false);
                navigate("/home");
              }}
            >
              Skip and go to dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}